import React, { useState, useRef, useEffect } from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Grid,
  Button,
  Chip,
  LinearProgress,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  List,
  ListItem,
  ListItemText,
  Divider,
  ImageList,
  ImageListItem,
  Tooltip,
  Fade,
  Backdrop,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  DirectionsCar,
  CheckCircle,
  Cancel,
  Visibility,
  PhotoLibrary,
  Person,
  VerifiedUser,
  Security,
  Image as ImageIcon,
  BrokenImage,
  Close,
  ZoomIn,
  ZoomOut,
  NavigateBefore,
  NavigateNext,
  RestartAlt,
  Collections,
  Edit,
  AttachMoney,
  VisibilityOff,
  Flight,
} from '@mui/icons-material';
import type { PendingVehicle } from '@/services/vehicleApprovalService';
import { vehicleApprovalService, CURRENCY_CONFIG, type CurrencyCode } from '@/services/vehicleApprovalService';
import { VehicleImageManagementDialog } from './VehicleImageManagementDialog';
import { toast } from 'react-hot-toast';
import { toggleVehicleActive } from '@/services/vehiclesService';
import { vehicleTypesService, type VehicleType, type VehicleBodyType } from '@/services/vehicleTypesService';
import { getApiBaseUrl } from '@/config/environment';
import ManageVehiclePricingDialog from '@/components/pricing/ManageVehiclePricingDialog';
import type { VehicleWithPricing } from '@/services/airportPricingService';

// Helper function to get currency symbol
const getCurrencySymbol = (currencyCode: string): string => {
  return CURRENCY_CONFIG[currencyCode as CurrencyCode]?.symbol || currencyCode;
};

// Helper function to get admin user ID from localStorage
const getAdminUserId = (): string => {
  const userStr = localStorage.getItem('user');
  if (userStr) {
    try {
      const user = JSON.parse(userStr);
      return user.id || user.userId || 'admin-default';
    } catch (e) {
    }
  }
  return 'admin-default';
};

interface VehicleApprovalCardProps {
  vehicle: PendingVehicle;
  onApprove: (vehicleId: string, notes?: string) => Promise<void>;
  onReject: (vehicleId: string, reason: string) => Promise<void>;
  onPricingUpdate?: () => void; // Callback to refresh vehicle data after pricing update
}

export const VehicleApprovalCard: React.FC<VehicleApprovalCardProps> = ({
  vehicle,
  onApprove,
  onReject,
  onPricingUpdate
}) => {
  // Local state for isActive (updated from toggle API response)
  const [localIsActive, setLocalIsActive] = useState<boolean | undefined>(vehicle.isActive);

  // Sync local state when vehicle prop changes
  useEffect(() => {
    setLocalIsActive(vehicle.isActive);
  }, [vehicle.isActive]);

  // Debug: Log vehicle data on render
  useEffect(() => {
    // Vehicle data tracked here
  }, [vehicle.id, vehicle.status, vehicle.pricing, localIsActive]);

  const [actionDialog, setActionDialog] = useState<{
    open: boolean;
    action: 'approve' | 'reject' | 'revert' | 'view' | null;
    notes: string;
  }>({
    open: false,
    action: null,
    notes: ''
  });

  const [imageViewer, setImageViewer] = useState<{
    open: boolean;
    currentIndex: number;
    zoom: number;
    showMagnifier: boolean;
  }>({
    open: false,
    currentIndex: 0,
    zoom: 1,
    showMagnifier: false
  });

  const [magnifierPosition, setMagnifierPosition] = useState({ x: 0, y: 0 });
  const imageRef = useRef<HTMLImageElement>(null);

  const [imageManagementDialog, setImageManagementDialog] = useState(false);

  const [pricingDialog, setPricingDialog] = useState<{
    open: boolean;
    selfDrive: string;
    withDriver: string;
    halfDaySelfDrive: string;
    halfDayWithDriver: string;
    fuelPrice: string;
    currency: string;
    saving: boolean;
  }>({
    open: false,
    selfDrive: String(vehicle.pricing?.selfDrive || ''),
    withDriver: String(vehicle.pricing?.withDriver || ''),
    halfDaySelfDrive: String(vehicle.pricing?.halfDaySelfDrive || ''),
    halfDayWithDriver: String(vehicle.pricing?.halfDayWithDriver || ''),
    fuelPrice: String(vehicle.pricing?.fuelPriceFullTank || vehicle.pricing?.fuelPrice || ''),
    currency: vehicle.pricing?.currency || 'NGN',
    saving: false
  });

  const [airportPricingDialog, setAirportPricingDialog] = useState(false);

  const [vehicleDetailsDialog, setVehicleDetailsDialog] = useState<{
    open: boolean;
    plateNumber: string;
    state: string;
    city: string;
    country: string;
    year: number;
    seatingCapacity: number;
    vehicleTypeId: string;
    bodyTypeId: string;
    saving: boolean;
  }>({
    open: false,
    plateNumber: vehicle.vehicle?.plateNumber || '',
    state: vehicle.vehicle?.state || '',
    city: vehicle.vehicle?.city || '',
    country: vehicle.vehicle?.country || '',
    year: vehicle.vehicle?.year || new Date().getFullYear(),
    seatingCapacity: vehicle.vehicle?.seatingCapacity || 4,
    vehicleTypeId: vehicle.vehicle?.vehicleTypeId || '',
    bodyTypeId: vehicle.vehicle?.bodyTypeId || '',
    saving: false
  });

  // Vehicle types and body types for dropdowns
  const [vehicleTypes, setVehicleTypes] = useState<VehicleType[]>([]);
  const [vehicleBodyTypes, setVehicleBodyTypes] = useState<VehicleBodyType[]>([]);

  // Load vehicle types and body types
  useEffect(() => {
    const loadTypes = async () => {
      try {
        const [types, bodyTypes] = await Promise.all([
          vehicleTypesService.getVehicleTypes(),
          vehicleTypesService.getVehicleBodyTypes()
        ]);
        setVehicleTypes(types.filter(t => t.isActive));
        setVehicleBodyTypes(bodyTypes.filter(t => t.isActive));
      } catch (error) {
        console.error('Failed to load vehicle types:', error);
      }
    };
    loadTypes();
  }, []);

  const [toggleActiveDialog, setToggleActiveDialog] = useState<{
    open: boolean;
    reason: string;
    toggling: boolean;
  }>({
    open: false,
    reason: '',
    toggling: false
  });

  // Update vehicle details when vehicle prop changes (after backend refresh)
  useEffect(() => {
    setVehicleDetailsDialog(prev => ({
      ...prev,
      plateNumber: vehicle.vehicle?.plateNumber || '',
      state: vehicle.vehicle?.state || '',
      city: vehicle.vehicle?.city || '',
      year: vehicle.vehicle?.year || new Date().getFullYear(),
      seatingCapacity: vehicle.vehicle?.seatingCapacity || 4,
      vehicleTypeId: vehicle.vehicle?.vehicleTypeId || '',
      bodyTypeId: vehicle.vehicle?.bodyTypeId || ''
    }));
  }, [vehicle.vehicle?.plateNumber, vehicle.vehicle?.state, vehicle.vehicle?.city, vehicle.vehicle?.year, vehicle.vehicle?.seatingCapacity, vehicle.vehicle?.vehicleTypeId, vehicle.vehicle?.bodyTypeId]);

  // Update pricing dialog when vehicle pricing changes (after backend refresh)
  useEffect(() => {
    if (!pricingDialog.open) {
      setPricingDialog(prev => ({
        ...prev,
        selfDrive: String(vehicle.pricing?.selfDrive || ''),
        withDriver: String(vehicle.pricing?.withDriver || ''),
        halfDaySelfDrive: String(vehicle.pricing?.halfDaySelfDrive || ''),
        halfDayWithDriver: String(vehicle.pricing?.halfDayWithDriver || ''),
        fuelPrice: String(vehicle.pricing?.fuelPriceFullTank || vehicle.pricing?.fuelPrice || ''),
        currency: vehicle.pricing?.currency || 'NGN'
      }));
    }
  }, [vehicle.pricing?.selfDrive, vehicle.pricing?.withDriver, vehicle.pricing?.halfDaySelfDrive, vehicle.pricing?.halfDayWithDriver, vehicle.pricing?.fuelPriceFullTank, vehicle.pricing?.fuelPrice, vehicle.pricing?.currency]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return 'success';
      case 'pending_approval': return 'warning';
      case 'rejected': return 'error';
      default: return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'pending_approval': return 'Pending Approval';
      case 'approved': return 'Approved';
      case 'rejected': return 'Rejected';
      default: return status;
    }
  };

  const handleAction = async () => {
    if (!actionDialog.action) {
      return;
    }

    try {
      if (actionDialog.action === 'approve') {
        await onApprove(vehicle.id, actionDialog.notes);
        setActionDialog({ open: false, action: null, notes: '' });
      } else if (actionDialog.action === 'reject') {
        await onReject(vehicle.id, actionDialog.notes);
        setActionDialog({ open: false, action: null, notes: '' });
      } else if (actionDialog.action === 'revert') {
        const adminUserId = getAdminUserId();
        const result = await vehicleApprovalService.revertToPending(
          vehicle.id,
          adminUserId,
          actionDialog.notes || 'Reverting to pending for re-review'
        );

        if (result.success) {
          toast.success('Vehicle reverted to pending review');
          setActionDialog({ open: false, action: null, notes: '' });
          // Trigger refresh by calling onApprove with empty callback
          if (onPricingUpdate) onPricingUpdate();
        } else {
          toast.error(result.error || 'Failed to revert vehicle');
        }
      }
    } catch (error) {
      console.error('❌ Error in handleAction:', error);
      toast.error(error instanceof Error ? error.message : 'Action failed');
      // Keep dialog open on error so user can retry
    }
  };

  const handleImageClick = (index: number) => {
    setImageViewer({ open: true, currentIndex: index, zoom: 1, showMagnifier: false });
  };

  const handleImageNavigation = (direction: 'prev' | 'next') => {
    const photosCount = vehicle.photos.items?.length || 0;
    if (photosCount === 0) return;

    setImageViewer(prev => ({
      ...prev,
      currentIndex: direction === 'next'
        ? (prev.currentIndex + 1) % photosCount
        : (prev.currentIndex - 1 + photosCount) % photosCount,
      zoom: 1,
      showMagnifier: false
    }));
  };

  const handleZoom = (delta: number) => {
    setImageViewer(prev => ({
      ...prev,
      zoom: Math.max(0.5, Math.min(5, prev.zoom + delta))
    }));
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!imageViewer.showMagnifier || !imageRef.current) return;

    const rect = imageRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;

    setMagnifierPosition({ x, y });
  };

  const handleOpenPricingDialog = async () => {
    // Fetch fresh pricing data from the dedicated pricing endpoint
    setPricingDialog(prev => ({ ...prev, open: true, saving: true }));

    try {
      const response = await fetch(`${getApiBaseUrl()}/api/v1/admin/vehicles/${vehicle.id}/pricing`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch pricing data');
      }

      const result = await response.json();
      const pricingData = result.data?.pricing;


      setPricingDialog({
        open: true,
        selfDrive: pricingData?.selfDrive ? String(pricingData.selfDrive) : '',
        withDriver: pricingData?.withDriver ? String(pricingData.withDriver) : '',
        halfDaySelfDrive: pricingData?.halfDaySelfDrive ? String(pricingData.halfDaySelfDrive) : '',
        halfDayWithDriver: pricingData?.halfDayWithDriver ? String(pricingData.halfDayWithDriver) : '',
        fuelPrice: pricingData?.fuelPriceFullTank ? String(pricingData.fuelPriceFullTank) : '',
        currency: pricingData?.currency || 'NGN',
        saving: false
      });
    } catch (error) {
      console.error('❌ Error fetching pricing:', error);
      toast.error('Failed to load pricing data');
      setPricingDialog(prev => ({ ...prev, saving: false }));
    }
  };

  const handleSavePricing = async () => {
    setPricingDialog(prev => ({ ...prev, saving: true }));

    try {
      const selfDrive = pricingDialog.selfDrive ? parseFloat(pricingDialog.selfDrive) : null;
      const withDriver = pricingDialog.withDriver ? parseFloat(pricingDialog.withDriver) : null;
      const halfDaySelfDrive = pricingDialog.halfDaySelfDrive ? parseFloat(pricingDialog.halfDaySelfDrive) : null;
      const halfDayWithDriver = pricingDialog.halfDayWithDriver ? parseFloat(pricingDialog.halfDayWithDriver) : null;
      const fuelPrice = pricingDialog.fuelPrice ? parseFloat(pricingDialog.fuelPrice) : null;

      const result = await vehicleApprovalService.updateVehiclePricing(vehicle.id, {
        selfDrive,
        withDriver,
        halfDaySelfDrive,
        halfDayWithDriver,
        fuelPrice,
        currency: pricingDialog.currency
      });

      if (result.success) {
        toast.success('Pricing updated successfully!');

        // Notify parent to refresh data from backend FIRST
        // This will reload the vehicle with updated pricing
        if (onPricingUpdate) {
          await onPricingUpdate();
        }

        // Wait a moment for React to re-render with fresh data
        await new Promise(resolve => setTimeout(resolve, 100));

        // Now close the dialog with fresh data
        setPricingDialog(prev => ({ ...prev, open: false, saving: false }));
      } else {
        console.error('❌ Failed to save pricing:', result.error);
        toast.error(result.error || 'Failed to update pricing');
        setPricingDialog(prev => ({ ...prev, saving: false }));
      }
    } catch (error) {
      console.error('❌ Error updating pricing:', error);
      toast.error('Failed to update pricing');
      setPricingDialog(prev => ({ ...prev, saving: false }));
    }
  };

  const handleSaveVehicleDetails = async () => {
    setVehicleDetailsDialog(prev => ({ ...prev, saving: true }));

    try {
      const updateData = {
        plateNumber: vehicleDetailsDialog.plateNumber,
        state: vehicleDetailsDialog.state,
        city: vehicleDetailsDialog.city,
        country: vehicleDetailsDialog.country,
        year: vehicleDetailsDialog.year,
        seatingCapacity: vehicleDetailsDialog.seatingCapacity,
        vehicleTypeId: vehicleDetailsDialog.vehicleTypeId || undefined,
        bodyTypeId: vehicleDetailsDialog.bodyTypeId || undefined
      };

      const result = await vehicleApprovalService.updateVehicleDetails(vehicle.id, updateData);

      if (result.success) {
        toast.success('Vehicle details updated successfully!');

        // Notify parent to refresh data from backend
        if (onPricingUpdate) {
          await onPricingUpdate();
        }

        // Wait for React to re-render with fresh data
        await new Promise(resolve => setTimeout(resolve, 200));

        // Close the dialog with fresh data
        setVehicleDetailsDialog(prev => ({ ...prev, open: false, saving: false }));
      } else {
        console.error('❌ Failed to save vehicle details:', result.error);
        toast.error(result.error || 'Failed to update vehicle details');
        setVehicleDetailsDialog(prev => ({ ...prev, saving: false }));
      }
    } catch (error) {
      console.error('❌ Error updating vehicle details:', error);
      toast.error('Failed to update vehicle details');
      setVehicleDetailsDialog(prev => ({ ...prev, saving: false }));
    }
  };

  const handleToggleActive = async () => {
    setToggleActiveDialog(prev => ({ ...prev, toggling: true }));

    try {
      // Default to true if undefined (vehicles are visible by default)
      const currentState = localIsActive ?? true;
      const newActiveState = !currentState;

      const result = await toggleVehicleActive(vehicle.id, {
        is_active: newActiveState,
        reason: toggleActiveDialog.reason || undefined
      });


      if (result.success) {
        // Update local state with the value from backend response
        if (result.data?.isActive !== undefined) {
          setLocalIsActive(result.data.isActive);
        }

        const message = result.data?.isActive
          ? 'Vehicle is now visible to renters'
          : 'Vehicle is now hidden from renters';
        toast.success(message);

        setToggleActiveDialog({ open: false, reason: '', toggling: false });
      } else {
        toast.error(result.error?.message || 'Failed to toggle vehicle visibility');
        setToggleActiveDialog(prev => ({ ...prev, toggling: false }));
      }
    } catch (error) {
      console.error('❌ Error toggling vehicle visibility:', error);
      toast.error('Failed to toggle vehicle visibility');
      setToggleActiveDialog(prev => ({ ...prev, toggling: false }));
    }
  };

  return (
    <>
      <Card sx={{ mb: 2, bgcolor: 'background.paper' }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}>
                <DirectionsCar />
              </Avatar>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {vehicle.vehicle.year} {vehicle.vehicle.make} {vehicle.vehicle.model}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {vehicle.vehicle.plateNumber} • {vehicle.vehicle.color}
                  {vehicle.vehicle.type && ` • ${vehicle.vehicle.type.charAt(0).toUpperCase() + vehicle.vehicle.type.slice(1)}`}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {vehicle.vehicle.transmission} • {vehicle.vehicle.fuelType}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'flex-end' }}>
              <Chip
                label={getStatusLabel(vehicle.status)}
                color={getStatusColor(vehicle.status) as any}
                size="small"
              />
              {vehicle.status === 'approved' && (() => {
                // Use local state, default to true if undefined (vehicles are visible by default)
                const isActive = localIsActive ?? true;
                return (
                  <Chip
                    icon={isActive ? <Visibility sx={{ fontSize: 16 }} /> : <VisibilityOff sx={{ fontSize: 16 }} />}
                    label={isActive ? 'Visible to Renters' : 'Hidden from Renters'}
                    color={isActive ? 'success' : 'default'}
                    size="small"
                    onClick={() => setToggleActiveDialog({ open: true, reason: '', toggling: false })}
                    sx={{ cursor: 'pointer', '&:hover': { opacity: 0.8 } }}
                  />
                );
              })()}
            </Box>
          </Box>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary">Host</Typography>
              <Typography variant="body2">{vehicle.host.name}</Typography>
              <Typography variant="caption" color="text.secondary">{vehicle.host.phone}</Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary">Seats</Typography>
              <Typography variant="body2">{vehicle.vehicle.seatingCapacity}</Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary">Insurance</Typography>
              <Typography variant="body2">{vehicle.insurance.type}</Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary">Photos</Typography>
              <Typography variant="body2">{vehicle.photos.count} uploaded</Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">
                Submitted: {new Date(vehicle.createdAt).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </Typography>
            </Grid>
          </Grid>

          {/* Vehicle Details Section */}
          <Box sx={{ mb: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                Vehicle Information
              </Typography>
              <IconButton
                size="small"
                onClick={() => setVehicleDetailsDialog({
                  open: true,
                  plateNumber: vehicle.vehicle.plateNumber || '',
                  state: vehicle.vehicle.state || '',
                  city: vehicle.vehicle.city || '',
                  country: vehicle.vehicle.country || '',
                  year: vehicle.vehicle.year || new Date().getFullYear(),
                  seatingCapacity: vehicle.vehicle.seatingCapacity || 4,
                  vehicleTypeId: vehicle.vehicle.vehicleTypeId || '',
                  bodyTypeId: vehicle.vehicle.bodyTypeId || '',
                  saving: false
                })}
                sx={{ color: 'primary.main' }}
                title="Edit vehicle details"
              >
                <Edit fontSize="small" />
              </IconButton>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  Plate: <strong>{vehicle.vehicle.plateNumber || 'Not set'}</strong>
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  Year: <strong>{vehicle.vehicle.year || 'Not set'}</strong>
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  Location: <strong>
                    {[vehicle.vehicle.city, vehicle.vehicle.state]
                      .filter(Boolean)
                      .join(', ') || 'Not set'}
                  </strong>
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="body2">
                    VIN: {vehicle.vehicle.vin && <strong>{vehicle.vehicle.vin}</strong>}
                  </Typography>
                  {!vehicle.vehicle.vin && (
                    <Chip label="Verified via documents" size="small" color="info" />
                  )}
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  Type: <strong>
                    {vehicle.vehicle.vehicleTypeId
                      ? vehicleTypes.find(t => t.id === vehicle.vehicle.vehicleTypeId)?.typeName || 'Not set'
                      : 'Not set'}
                  </strong>
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  Body: <strong>
                    {vehicle.vehicle.bodyTypeId
                      ? vehicleBodyTypes.find(t => t.id === vehicle.vehicle.bodyTypeId)?.bodyName || 'Not set'
                      : 'Not set'}
                  </strong>
                </Typography>
              </Grid>
            </Grid>
          </Box>

          {/* Proof of Ownership Section - Shown when VIN is NOT provided (user provided documents instead) */}
          {vehicle.proofOfOwnership && vehicle.proofOfOwnership.count > 0 && !vehicle.vehicle.hasVin && (
            <Box sx={{ mb: 2, p: 1.5, bgcolor: 'info.lighter', borderRadius: 1, border: '1px solid', borderColor: 'info.main' }}>
              <Typography variant="caption" color="info.dark" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 1 }}>
                📄 Ownership Documents (VIN Not Provided)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {vehicle.proofOfOwnership.count} document(s) uploaded - Click to view
              </Typography>
              {vehicle.proofOfOwnership.items.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {vehicle.proofOfOwnership.items.map((doc) => (
                    <Chip
                      key={doc.id}
                      label={doc.type ? doc.type.replace(/_/g, ' ').toUpperCase() : 'DOCUMENT'}
                      size="small"
                      color="info"
                      onClick={() => {
                        // Open document in preview
                        const img = new Image();
                        img.src = doc.url;
                        const w = window.open('');
                        w?.document.write(img.outerHTML);
                      }}
                      sx={{ cursor: 'pointer', '&:hover': { opacity: 0.8 } }}
                    />
                  ))}
                </Box>
              )}
            </Box>
          )}


          {/* Pricing Information */}
          <Box sx={{ mb: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Daily Rates
              </Typography>
              <IconButton
                size="small"
                onClick={handleOpenPricingDialog}
                sx={{ color: 'primary.main' }}
                title="Edit pricing"
              >
                <Edit fontSize="small" />
              </IconButton>
            </Box>
            <Grid container spacing={1.5}>
              <Grid item xs={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <DirectionsCar sx={{ fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant="body2" sx={{ fontWeight: 500, color: vehicle.pricing?.selfDrive ? 'text.primary' : 'text.disabled' }}>
                    {vehicle.pricing?.selfDrive
                      ? `${getCurrencySymbol(vehicle.pricing.currency)}${vehicle.pricing.selfDrive.toLocaleString()}`
                      : 'Not set'}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Person sx={{ fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant="body2" sx={{ fontWeight: 500, color: vehicle.pricing?.withDriver ? 'text.primary' : 'text.disabled' }}>
                    {vehicle.pricing?.withDriver
                      ? `${getCurrencySymbol(vehicle.pricing.currency)}${vehicle.pricing.withDriver.toLocaleString()}`
                      : 'Not set'}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Airport Pricing Section */}
          <Box sx={{
            mb: 2,
            p: 1.5,
            bgcolor: 'action.hover',
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'primary.main',
            borderWidth: '2px'
          }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Flight sx={{ fontSize: 18, color: 'primary.main' }} />
                <Typography variant="caption" color="text.primary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Airport Transfer Pricing
                </Typography>
              </Box>
              <Button
                size="small"
                variant="contained"
                color="primary"
                startIcon={<Flight />}
                onClick={() => setAirportPricingDialog(true)}
                sx={{ fontSize: '0.75rem', py: 0.5, px: 1.5 }}
              >
                Manage
              </Button>
            </Box>
            <Typography variant="caption" color="text.primary" sx={{ opacity: 0.8 }}>
              Set pricing for airport pickup/dropoff zones
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 0.5, mb: 2, flexWrap: 'wrap' }}>
            {vehicle.insurance.hasGPS && (
              <Chip
                label="GPS"
                size="small"
                color="success"
                variant="outlined"
                icon={<CheckCircle />}
                sx={{ fontSize: '0.7rem', height: 24 }}
              />
            )}
            {vehicle.insurance.hasInspectionCertificate && (
              <Chip
                label="Inspection"
                size="small"
                color="success"
                variant="outlined"
                icon={<VerifiedUser />}
                sx={{ fontSize: '0.7rem', height: 24 }}
              />
            )}
            {vehicle.instantBooking && (
              <Chip
                label="Instant Booking"
                size="small"
                color="info"
                variant="outlined"
                sx={{ fontSize: '0.7rem', height: 24 }}
              />
            )}
          </Box>

          {/* Manage Images - Available for all vehicles */}
          <Box sx={{ mb: 1 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Collections />}
              onClick={() => setImageManagementDialog(true)}
            >
              Manage Images
            </Button>
          </Box>

          {/* Show action buttons for vehicles that need approval (not already approved/rejected) */}
          {vehicle.status !== 'approved' && vehicle.status !== 'rejected' && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<Visibility />}
                onClick={() => {
                  setActionDialog({ open: true, action: 'view', notes: '' });
                }}
              >
                View Details & Photos
              </Button>
              <Box sx={{ flex: 1 }} />
              <Button
                variant="contained"
                color="success"
                size="small"
                startIcon={<CheckCircle />}
                onClick={() => {
                  setActionDialog({ open: true, action: 'approve', notes: '' });
                }}
              >
                Approve
              </Button>
              <Button
                variant="outlined"
                color="error"
                size="small"
                startIcon={<Cancel />}
                onClick={() => {
                  setActionDialog({ open: true, action: 'reject', notes: '' });
                }}
              >
                Reject
              </Button>
            </Box>
          )}

          {/* Show revert button for approved/rejected vehicles */}
          {(vehicle.status === 'approved' || vehicle.status === 'rejected') && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2 }}>
              <Button
                variant="outlined"
                color="warning"
                size="small"
                startIcon={<RestartAlt />}
                onClick={() => {
                  setActionDialog({ open: true, action: 'revert', notes: '' });
                }}
              >
                Revert to Pending
              </Button>
              <Typography variant="caption" color="text.secondary" sx={{ ml: 1, alignSelf: 'center' }}>
                Undo {vehicle.status === 'approved' ? 'approval' : 'rejection'} and return to pending review
              </Typography>
            </Box>
          )}

          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Submitted: {new Date(vehicle.createdAt).toLocaleDateString()}
          </Typography>
        </CardContent>
      </Card>

      {/* Action/View Dialog */}
      <Dialog
        open={actionDialog.open}
        onClose={() => setActionDialog({ open: false, action: null, notes: '' })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {actionDialog.action === 'approve' && 'Approve Vehicle'}
          {actionDialog.action === 'reject' && 'Reject Vehicle'}
          {actionDialog.action === 'view' && 'Vehicle Details'}
        </DialogTitle>
        <DialogContent>
          {actionDialog.action === 'view' ? (
            <Box>
              <Typography variant="h6" gutterBottom>
                {vehicle.vehicle.year} {vehicle.vehicle.make} {vehicle.vehicle.model}
              </Typography>

              <List>
                <ListItem>
                  <ListItemText primary="Plate Number" secondary={vehicle.vehicle.plateNumber} />
                </ListItem>
                <ListItem>
                  <ListItemText primary="Color" secondary={vehicle.vehicle.color} />
                </ListItem>
                {vehicle.vehicle.type && (
                  <ListItem>
                    <ListItemText primary="Vehicle Type" secondary={vehicle.vehicle.type.charAt(0).toUpperCase() + vehicle.vehicle.type.slice(1)} />
                  </ListItem>
                )}
                <ListItem>
                  <ListItemText primary="Transmission" secondary={vehicle.vehicle.transmission} />
                </ListItem>
                <ListItem>
                  <ListItemText primary="Fuel Type" secondary={vehicle.vehicle.fuelType} />
                </ListItem>
                <ListItem>
                  <ListItemText primary="Seating Capacity" secondary={vehicle.vehicle.seatingCapacity} />
                </ListItem>
              </List>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" gutterBottom>Insurance Information</Typography>
              <List dense>
                <ListItem>
                  <ListItemText primary="Type" secondary={vehicle.insurance.type} />
                </ListItem>
                <ListItem>
                  <ListItemText primary="Payment Term" secondary={vehicle.insurance.paymentTerm} />
                </ListItem>
                <ListItem>
                  <ListItemText primary="GPS Tracking" secondary={vehicle.insurance.hasGPS ? 'Yes' : 'No'} />
                </ListItem>
                <ListItem>
                  <ListItemText primary="Inspection Certificate" secondary={vehicle.insurance.hasInspectionCertificate ? 'Yes' : 'No'} />
                </ListItem>
              </List>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" gutterBottom>Host Information</Typography>
              <List dense>
                <ListItem>
                  <ListItemText primary="Name" secondary={vehicle.host.name} />
                </ListItem>
                <ListItem>
                  <ListItemText primary="Phone" secondary={vehicle.host.phone} />
                </ListItem>
                {vehicle.host.email && (
                  <ListItem>
                    <ListItemText primary="Email" secondary={vehicle.host.email} />
                  </ListItem>
                )}
              </List>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PhotoLibrary />
                Photos ({vehicle.photos.count})
              </Typography>

              {vehicle.photos.count > 0 ? (
                <Box sx={{ mt: 2 }}>
                  <ImageList cols={3} gap={8} sx={{ maxHeight: 400 }}>
                    {vehicle.photos.items?.map((photo: any, index: number) => {
                      // Use the URL directly from API response (base64 or http URL)
                      const photoUrl = photo.url || `https://polarride-api-17dr.vercel.app/api/photos/${photo.id}`;

                      return (
                        <ImageListItem key={photo.id}>
                          <Box
                            onClick={() => handleImageClick(index)}
                            sx={{
                              width: '100%',
                              height: 150,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: 'grey.100',
                              borderRadius: 1,
                              overflow: 'hidden',
                              position: 'relative',
                              cursor: 'pointer',
                              transition: 'transform 0.2s, box-shadow 0.2s',
                              '&:hover': {
                                transform: 'scale(1.05)',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                '& .zoom-overlay': {
                                  opacity: 1
                                }
                              }
                            }}
                          >
                            <img
                              src={photoUrl}
                              alt={`${vehicle.vehicle.make} ${vehicle.vehicle.model} - ${photo.type || photo.category || 'photo'}`}
                              loading="lazy"
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover'
                              }}
                              onError={(e) => {
                                // Show placeholder if image fails to load
                                e.currentTarget.style.display = 'none';
                                if (e.currentTarget.nextSibling) {
                                  (e.currentTarget.nextSibling as HTMLElement).style.display = 'flex';
                                }
                              }}
                            />
                            <Box
                              className="zoom-overlay"
                              sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: 'rgba(0,0,0,0.5)',
                                opacity: 0,
                                transition: 'opacity 0.2s'
                              }}
                            >
                              <ZoomIn sx={{ color: 'white', fontSize: 40 }} />
                            </Box>
                            <Box
                              sx={{
                                display: 'none',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                position: 'absolute',
                                width: '100%',
                                height: '100%',
                                backgroundColor: 'grey.200'
                              }}
                            >
                              <BrokenImage sx={{ fontSize: 40, color: 'grey.500' }} />
                              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                                {(photo.type || photo.category || 'photo').replace('_', ' ')}
                              </Typography>
                            </Box>
                          </Box>
                          <Tooltip title="Click to view full size">
                            <Chip
                              label={(photo.type || photo.category || 'photo').replace('_', ' ')}
                              size="small"
                              sx={{ mt: 0.5, fontSize: '0.7rem' }}
                              color={photo.isPrimary ? 'primary' : 'default'}
                            />
                          </Tooltip>
                        </ImageListItem>
                      );
                    })}
                  </ImageList>
                </Box>
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    py: 4,
                    backgroundColor: 'grey.50',
                    borderRadius: 1,
                    mt: 2
                  }}
                >
                  <ImageIcon sx={{ fontSize: 48, color: 'grey.400', mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    No photos uploaded
                  </Typography>
                </Box>
              )}
            </Box>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {actionDialog.action === 'approve'
                  ? `Are you sure you want to approve this ${vehicle.vehicle.make} ${vehicle.vehicle.model}?`
                  : `Please provide a reason for rejecting this vehicle registration.`
                }
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                label={actionDialog.action === 'approve' ? 'Notes (Optional)' : 'Rejection Reason *'}
                value={actionDialog.notes}
                onChange={(e) => setActionDialog(prev => ({ ...prev, notes: e.target.value }))}
                placeholder={actionDialog.action === 'approve'
                  ? 'Add any notes or instructions...'
                  : 'Explain why this vehicle is being rejected...'
                }
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActionDialog({ open: false, action: null, notes: '' })}>
            {actionDialog.action === 'view' ? 'Close' : 'Cancel'}
          </Button>
          {actionDialog.action !== 'view' && (
            <Button
              variant="contained"
              color={
                actionDialog.action === 'approve' ? 'success' :
                actionDialog.action === 'revert' ? 'warning' :
                'error'
              }
              onClick={handleAction}
              disabled={actionDialog.action === 'reject' && !actionDialog.notes.trim()}
            >
              {actionDialog.action === 'approve' ? 'Approve' :
               actionDialog.action === 'revert' ? 'Revert' :
               'Reject'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Image Viewer Dialog with Zoom and Magnifier */}
      <Dialog
        open={imageViewer.open}
        onClose={() => setImageViewer({ open: false, currentIndex: 0, zoom: 1, showMagnifier: false })}
        maxWidth={false}
        fullScreen
        TransitionComponent={Fade}
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.95)'
          }
        }}
      >
        <Box sx={{ position: 'relative', width: '100%', height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {/* Close Button */}
          <IconButton
            onClick={() => setImageViewer({ open: false, currentIndex: 0, zoom: 1, showMagnifier: false })}
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              color: 'white',
              backgroundColor: 'rgba(0,0,0,0.5)',
              '&:hover': { backgroundColor: 'rgba(0,0,0,0.7)' },
              zIndex: 2
            }}
          >
            <Close />
          </IconButton>

          {/* Photo Info */}
          {vehicle.photos.items && vehicle.photos.items[imageViewer.currentIndex] && (
            <Box sx={{ position: 'absolute', top: 16, left: 16, zIndex: 2 }}>
              <Typography variant="h6" sx={{ color: 'white', textShadow: '0 2px 4px rgba(0,0,0,0.8)' }}>
                {vehicle.vehicle.year} {vehicle.vehicle.make} {vehicle.vehicle.model}
              </Typography>
              <Chip
                label={(vehicle.photos.items[imageViewer.currentIndex].type || vehicle.photos.items[imageViewer.currentIndex].category || 'photo').replace('_', ' ')}
                size="small"
                color="primary"
                sx={{ mt: 1 }}
              />
              <Typography variant="caption" sx={{ color: 'white', display: 'block', mt: 1 }}>
                Photo {imageViewer.currentIndex + 1} of {vehicle.photos.count}
              </Typography>
            </Box>
          )}

          {/* Zoom Controls */}
          <Box sx={{ position: 'absolute', top: 16, right: 80, display: 'flex', gap: 1, zIndex: 2 }}>
            <Tooltip title="Zoom Out">
              <IconButton
                onClick={() => handleZoom(-0.25)}
                disabled={imageViewer.zoom <= 0.5}
                sx={{ color: 'white', backgroundColor: 'rgba(0,0,0,0.5)', '&:hover': { backgroundColor: 'rgba(0,0,0,0.7)' } }}
              >
                <ZoomOut />
              </IconButton>
            </Tooltip>
            <Chip label={`${Math.round(imageViewer.zoom * 100)}%`} sx={{ color: 'white', backgroundColor: 'rgba(0,0,0,0.5)' }} />
            <Tooltip title="Zoom In">
              <IconButton
                onClick={() => handleZoom(0.25)}
                disabled={imageViewer.zoom >= 5}
                sx={{ color: 'white', backgroundColor: 'rgba(0,0,0,0.5)', '&:hover': { backgroundColor: 'rgba(0,0,0,0.7)' } }}
              >
                <ZoomIn />
              </IconButton>
            </Tooltip>
            <Tooltip title="Reset Zoom">
              <IconButton
                onClick={() => handleZoom(1 - imageViewer.zoom)}
                sx={{ color: 'white', backgroundColor: 'rgba(0,0,0,0.5)', '&:hover': { backgroundColor: 'rgba(0,0,0,0.7)' } }}
              >
                <RestartAlt />
              </IconButton>
            </Tooltip>
            <Tooltip title={imageViewer.showMagnifier ? "Disable Magnifier" : "Enable Magnifier"}>
              <IconButton
                onClick={() => setImageViewer(prev => ({ ...prev, showMagnifier: !prev.showMagnifier }))}
                sx={{
                  color: imageViewer.showMagnifier ? 'primary.main' : 'white',
                  backgroundColor: 'rgba(0,0,0,0.5)',
                  '&:hover': { backgroundColor: 'rgba(0,0,0,0.7)' }
                }}
              >
                <PhotoLibrary />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Navigation Buttons */}
          {vehicle.photos.count > 1 && (
            <>
              <IconButton
                onClick={() => handleImageNavigation('prev')}
                sx={{
                  position: 'absolute',
                  left: 16,
                  color: 'white',
                  backgroundColor: 'rgba(0,0,0,0.5)',
                  '&:hover': { backgroundColor: 'rgba(0,0,0,0.7)' },
                  zIndex: 2
                }}
              >
                <NavigateBefore fontSize="large" />
              </IconButton>
              <IconButton
                onClick={() => handleImageNavigation('next')}
                sx={{
                  position: 'absolute',
                  right: 16,
                  color: 'white',
                  backgroundColor: 'rgba(0,0,0,0.5)',
                  '&:hover': { backgroundColor: 'rgba(0,0,0,0.7)' },
                  zIndex: 2
                }}
              >
                <NavigateNext fontSize="large" />
              </IconButton>
            </>
          )}

          {/* Main Image */}
          {vehicle.photos.items && vehicle.photos.items[imageViewer.currentIndex] && (
            <Box
              onMouseMove={handleMouseMove}
              sx={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                height: '100%',
                overflow: 'auto',
                cursor: imageViewer.showMagnifier ? 'none' : 'default'
              }}
            >
              <img
                ref={imageRef}
                src={vehicle.photos.items[imageViewer.currentIndex].url || `https://polarride-api-17dr.vercel.app/api/photos/${vehicle.photos.items[imageViewer.currentIndex].id}`}
                alt={`${vehicle.vehicle.make} ${vehicle.vehicle.model}`}
                style={{
                  maxWidth: '90%',
                  maxHeight: '90%',
                  transform: `scale(${imageViewer.zoom})`,
                  transition: 'transform 0.2s',
                  objectFit: 'contain'
                }}
              />

              {/* Magnifier Lens */}
              {imageViewer.showMagnifier && imageRef.current && (
                <Box
                  sx={{
                    position: 'fixed',
                    width: 200,
                    height: 200,
                    border: '3px solid white',
                    borderRadius: '50%',
                    pointerEvents: 'none',
                    overflow: 'hidden',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
                    zIndex: 3,
                    left: `calc(${magnifierPosition.x}% - 100px)`,
                    top: `calc(${magnifierPosition.y}% - 100px)`,
                    backgroundImage: `url(${vehicle.photos.items[imageViewer.currentIndex].url || `https://polarride-api-17dr.vercel.app/api/photos/${vehicle.photos.items[imageViewer.currentIndex].id}`})`,
                    backgroundSize: `${imageRef.current.width * 2.5}px ${imageRef.current.height * 2.5}px`,
                    backgroundPosition: `${-magnifierPosition.x * imageRef.current.width * 2.5 / 100 + 100}px ${-magnifierPosition.y * imageRef.current.height * 2.5 / 100 + 100}px`,
                    backgroundRepeat: 'no-repeat'
                  }}
                />
              )}
            </Box>
          )}

          {/* Thumbnail Strip */}
          {vehicle.photos.count > 1 && (
            <Box
              sx={{
                position: 'absolute',
                bottom: 16,
                left: '50%',
                transform: 'translateX(-50%)',
                display: 'flex',
                gap: 1,
                backgroundColor: 'rgba(0,0,0,0.7)',
                padding: 1,
                borderRadius: 2,
                maxWidth: '90%',
                overflowX: 'auto',
                zIndex: 2
              }}
            >
              {vehicle.photos.items?.map((photo: any, index: number) => (
                <Box
                  key={photo.id}
                  onClick={() => setImageViewer(prev => ({ ...prev, currentIndex: index, zoom: 1 }))}
                  sx={{
                    width: 80,
                    height: 60,
                    flexShrink: 0,
                    cursor: 'pointer',
                    border: index === imageViewer.currentIndex ? '3px solid white' : '3px solid transparent',
                    borderRadius: 1,
                    overflow: 'hidden',
                    opacity: index === imageViewer.currentIndex ? 1 : 0.6,
                    transition: 'all 0.2s',
                    '&:hover': {
                      opacity: 1,
                      transform: 'scale(1.1)'
                    }
                  }}
                >
                  <img
                    src={photo.url || `https://polarride-api-17dr.vercel.app/api/photos/${photo.id}`}
                    alt={`Thumbnail ${index + 1}`}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Dialog>

      {/* Vehicle Image Management Dialog */}
      <VehicleImageManagementDialog
        open={imageManagementDialog}
        onClose={() => setImageManagementDialog(false)}
        vehicleId={vehicle.id}
        vehicleName={`${vehicle.vehicle.year} ${vehicle.vehicle.make} ${vehicle.vehicle.model}`}
      />

      {/* Pricing Edit Dialog */}
      <Dialog
        open={pricingDialog.open}
        onClose={() => !pricingDialog.saving && setPricingDialog(prev => ({ ...prev, open: false }))}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AttachMoney />
            Edit Vehicle Pricing
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Set the daily rental rates for {vehicle.vehicle.year} {vehicle.vehicle.make} {vehicle.vehicle.model}
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth disabled={pricingDialog.saving}>
                <InputLabel>Currency</InputLabel>
                <Select
                  value={pricingDialog.currency}
                  label="Currency"
                  onChange={(e) => setPricingDialog(prev => ({ ...prev, currency: e.target.value }))}
                >
                  {Object.entries(CURRENCY_CONFIG).map(([code, config]) => (
                    <MenuItem key={code} value={code}>
                      {config.symbol} - {config.name} ({code})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Self-Drive Rate (per day)"
                type="number"
                value={pricingDialog.selfDrive}
                onChange={(e) => setPricingDialog(prev => ({ ...prev, selfDrive: e.target.value }))}
                InputProps={{
                  startAdornment: <InputAdornment position="start">{getCurrencySymbol(pricingDialog.currency)}</InputAdornment>,
                }}
                placeholder="e.g., 35000"
                disabled={pricingDialog.saving}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="With Driver Rate (per day)"
                type="number"
                value={pricingDialog.withDriver}
                onChange={(e) => setPricingDialog(prev => ({ ...prev, withDriver: e.target.value }))}
                InputProps={{
                  startAdornment: <InputAdornment position="start">{getCurrencySymbol(pricingDialog.currency)}</InputAdornment>,
                }}
                placeholder="e.g., 45000"
                disabled={pricingDialog.saving}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Half-Day Self-Drive (optional)"
                type="number"
                value={pricingDialog.halfDaySelfDrive}
                onChange={(e) => setPricingDialog(prev => ({ ...prev, halfDaySelfDrive: e.target.value }))}
                InputProps={{
                  startAdornment: <InputAdornment position="start">{getCurrencySymbol(pricingDialog.currency)}</InputAdornment>,
                }}
                placeholder="e.g., 20000"
                disabled={pricingDialog.saving}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Half-Day With Driver (optional)"
                type="number"
                value={pricingDialog.halfDayWithDriver}
                onChange={(e) => setPricingDialog(prev => ({ ...prev, halfDayWithDriver: e.target.value }))}
                InputProps={{
                  startAdornment: <InputAdornment position="start">{getCurrencySymbol(pricingDialog.currency)}</InputAdornment>,
                }}
                placeholder="e.g., 25000"
                disabled={pricingDialog.saving}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Fuel Price (optional)"
                type="number"
                value={pricingDialog.fuelPrice}
                onChange={(e) => setPricingDialog(prev => ({ ...prev, fuelPrice: e.target.value }))}
                InputProps={{
                  startAdornment: <InputAdornment position="start">{getCurrencySymbol(pricingDialog.currency)}</InputAdornment>,
                }}
                placeholder="e.g., 5000"
                disabled={pricingDialog.saving}
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 2, p: 1.5, bgcolor: 'info.dark', borderRadius: 1, border: '1px solid', borderColor: 'info.main', opacity: 0.8 }}>
            <Typography variant="caption" color="text.secondary">
              💡 Tip: With-driver rates are typically 20-30% higher than self-drive rates to cover driver costs.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setPricingDialog(prev => ({ ...prev, open: false }))}
            disabled={pricingDialog.saving}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSavePricing}
            disabled={pricingDialog.saving}
          >
            {pricingDialog.saving ? 'Saving...' : 'Save Pricing'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Vehicle Details Edit Dialog */}
      <Dialog
        open={vehicleDetailsDialog.open}
        onClose={() => !vehicleDetailsDialog.saving && setVehicleDetailsDialog(prev => ({ ...prev, open: false }))}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DirectionsCar />
            Edit Vehicle Details
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Update the registration details for {vehicle.vehicle.year} {vehicle.vehicle.make} {vehicle.vehicle.model}
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Plate Number"
                value={vehicleDetailsDialog.plateNumber}
                onChange={(e) => setVehicleDetailsDialog(prev => ({ ...prev, plateNumber: e.target.value }))}
                placeholder="e.g., ABC-123-XYZ"
                disabled={vehicleDetailsDialog.saving}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="number"
                label="Year"
                value={vehicleDetailsDialog.year}
                onChange={(e) => setVehicleDetailsDialog(prev => ({ ...prev, year: parseInt(e.target.value) || new Date().getFullYear() }))}
                placeholder="e.g., 2020"
                disabled={vehicleDetailsDialog.saving}
                inputProps={{
                  min: 1900,
                  max: new Date().getFullYear() + 1
                }}
                helperText={`Valid range: 1900 - ${new Date().getFullYear() + 1}`}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="number"
                label="Seating Capacity"
                value={vehicleDetailsDialog.seatingCapacity}
                onChange={(e) => setVehicleDetailsDialog(prev => ({ ...prev, seatingCapacity: parseInt(e.target.value) || 4 }))}
                placeholder="e.g., 5"
                disabled={vehicleDetailsDialog.saving}
                inputProps={{
                  min: 1,
                  max: 15
                }}
                helperText="Number of passengers (1-15)"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth disabled={vehicleDetailsDialog.saving}>
                <InputLabel>Vehicle Type (Rental Tier)</InputLabel>
                <Select
                  value={vehicleDetailsDialog.vehicleTypeId}
                  onChange={(e) => setVehicleDetailsDialog(prev => ({ ...prev, vehicleTypeId: e.target.value }))}
                  label="Vehicle Type (Rental Tier)"
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {vehicleTypes.map((type) => (
                    <MenuItem key={type.id} value={type.id}>
                      {type.typeName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth disabled={vehicleDetailsDialog.saving}>
                <InputLabel>Body Type (Physical Form)</InputLabel>
                <Select
                  value={vehicleDetailsDialog.bodyTypeId}
                  onChange={(e) => setVehicleDetailsDialog(prev => ({ ...prev, bodyTypeId: e.target.value }))}
                  label="Body Type (Physical Form)"
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {vehicleBodyTypes.map((type) => (
                    <MenuItem key={type.id} value={type.id}>
                      {type.bodyName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="City"
                value={vehicleDetailsDialog.city}
                onChange={(e) => setVehicleDetailsDialog(prev => ({ ...prev, city: e.target.value }))}
                placeholder="e.g., Ikeja, Westlands, Osu"
                disabled={vehicleDetailsDialog.saving}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="State"
                value={vehicleDetailsDialog.state}
                onChange={(e) => setVehicleDetailsDialog(prev => ({ ...prev, state: e.target.value }))}
                placeholder="e.g., Lagos, Nairobi, Accra"
                disabled={vehicleDetailsDialog.saving}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Country"
                value={vehicleDetailsDialog.country}
                onChange={(e) => setVehicleDetailsDialog(prev => ({ ...prev, country: e.target.value }))}
                placeholder="e.g., Nigeria, Kenya, Ghana"
                disabled={vehicleDetailsDialog.saving}
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 2, p: 1.5, bgcolor: 'warning.dark', borderRadius: 1, border: '1px solid', borderColor: 'warning.main', opacity: 0.8 }}>
            <Typography variant="caption" color="text.secondary">
              ⚠️ Note: VIN (Vehicle Identification Number) cannot be edited for security reasons.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setVehicleDetailsDialog(prev => ({ ...prev, open: false }))}
            disabled={vehicleDetailsDialog.saving}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveVehicleDetails}
            disabled={vehicleDetailsDialog.saving}
          >
            {vehicleDetailsDialog.saving ? 'Saving...' : 'Save Details'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toggle Active/Inactive Dialog */}
      <Dialog
        open={toggleActiveDialog.open}
        onClose={() => !toggleActiveDialog.toggling && setToggleActiveDialog({ open: false, reason: '', toggling: false })}
        maxWidth="sm"
        fullWidth
      >
        {(() => {
          // Use local state, default to true if undefined (vehicles are visible by default)
          const isActive = localIsActive ?? true;
          return (
            <>
              <DialogTitle>
                {isActive ? 'Hide Vehicle from Renters' : 'Show Vehicle to Renters'}
              </DialogTitle>
              <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  {isActive
                    ? 'This vehicle will be hidden from renters and unavailable for booking.'
                    : 'This vehicle will be visible to renters and available for booking.'}
                </Typography>

                {!isActive && (
                  <Typography variant="body2" sx={{ fontWeight: 600, color: 'warning.main', mb: 2 }}>
                    {vehicle.vehicle.year} {vehicle.vehicle.make} {vehicle.vehicle.model}
                  </Typography>
                )}

                <TextField
                  fullWidth
                  label={isActive ? "Reason for hiding (optional)" : "Reason for showing (optional)"}
                  multiline
                  rows={3}
                  value={toggleActiveDialog.reason}
                  onChange={(e) => setToggleActiveDialog(prev => ({ ...prev, reason: e.target.value }))}
                  placeholder={isActive ? "e.g., Vehicle needs maintenance" : "e.g., Maintenance completed"}
                  disabled={toggleActiveDialog.toggling}
                />
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={() => setToggleActiveDialog({ open: false, reason: '', toggling: false })}
                  disabled={toggleActiveDialog.toggling}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color={isActive ? 'warning' : 'success'}
                  onClick={handleToggleActive}
                  disabled={toggleActiveDialog.toggling}
                >
                  {toggleActiveDialog.toggling ? 'Updating...' : (isActive ? 'Hide Vehicle' : 'Show Vehicle')}
                </Button>
              </DialogActions>
            </>
          );
        })()}
      </Dialog>

      {/* Airport Pricing Dialog */}
      <ManageVehiclePricingDialog
        open={airportPricingDialog}
        vehicle={{
          vehicleId: vehicle.id,
          plateNumber: vehicle.vehicle.plateNumber,
          vehicleType: vehicle.vehicle.type,
          vehicleMake: vehicle.vehicle.make,
          vehicleModel: vehicle.vehicle.model,
          vehicleYear: vehicle.vehicle.year,
          hostId: vehicle.host.id,
          hostName: `${vehicle.host.firstName} ${vehicle.host.lastName}`,
          airportPricingCount: 0,
          hasAirportPricing: false,
        }}
        onClose={() => setAirportPricingDialog(false)}
        onSuccess={() => {
          setAirportPricingDialog(false);
          toast.success('Airport pricing updated successfully');
        }}
      />
    </>
  );
};
